import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { AuthShell, AuthError } from '../components/auth/AuthShell';
import { useI18n } from '../contexts/I18nContext';
import { ApiError } from '../lib/api';
import { approveConsent, denyConsent, fetchConsent } from '../lib/oauthApi';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// /oauth/consent — Firmio MCP authorization screen.
//
// An MCP client (Claude Desktop, claude.ai, Claude Code) sent the browser to
// /api/oauth/authorize; the backend validated the client and redirected here
// with the whole request signed into `?req=`. The route is protected, so an
// anonymous visitor goes through the regular login first and comes back with
// the query string intact (LoginPage keeps `from.search`). Approving creates a
// single-use code and sends the browser back to the client; the tokens it then
// obtains act with exactly this person's permissions.
// ---------------------------------------------------------------------------

export function OAuthConsentPage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const req = params.get('req') ?? '';
  const user = useAuthStore((s) => s.user);
  const [busy, setBusy] = useState<'approve' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  const consent = useQuery({
    queryKey: ['oauth-consent', req],
    queryFn: () => fetchConsent(req),
    enabled: req.length > 0,
    retry: false,
  });

  // No `req` at all: nobody should land here by hand.
  useEffect(() => {
    if (!req) setError(t('oauth.consent.missing'));
  }, [req, t]);

  async function decide(kind: 'approve' | 'deny') {
    setBusy(kind);
    setError(null);
    try {
      const redirectTo = kind === 'approve' ? await approveConsent(req) : await denyConsent(req);
      setRedirecting(true);
      window.location.assign(redirectTo);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('oauth.consent.failed'));
      setBusy(null);
    }
  }

  const clientName = consent.data?.client.name || t('oauth.consent.unknownClient');
  const loadError =
    consent.error instanceof ApiError
      ? consent.error.message
      : consent.error
        ? t('oauth.consent.failed')
        : null;

  return (
    <AuthShell title={t('oauth.consent.title')}>
      {(error || loadError) && <AuthError message={error ?? loadError ?? ''} />}

      {consent.isLoading && req && (
        <p className="text-sm" style={{ color: '#94A3B8' }}>
          {t('common.loading')}
        </p>
      )}

      {consent.data && (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            {consent.data.client.logoUri ? (
              <img
                src={consent.data.client.logoUri}
                alt=""
                className="h-10 w-10 shrink-0 rounded-lg object-cover"
              />
            ) : (
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#A78BFA' }}
              >
                <ShieldCheck size={22} />
              </span>
            )}
            <div className="min-w-0">
              <p className="text-sm" style={{ color: '#F1F5F9' }}>
                <strong>{clientName}</strong> {t('oauth.consent.asks')}
              </p>
              <p className="mt-0.5 truncate text-xs" style={{ color: '#64748B' }}>
                {consent.data.client.uri ?? consent.data.redirectHost}
              </p>
            </div>
          </div>

          <div
            className="rounded-[10px] px-3 py-2 text-sm"
            style={{
              background: 'rgba(59, 130, 246, 0.08)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              color: '#CBD5E1',
            }}
          >
            <p>
              {t('oauth.consent.account')}{' '}
              <strong style={{ color: '#F1F5F9' }}>{user?.email}</strong>
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs" style={{ color: '#94A3B8' }}>
              <li>{t('oauth.consent.scopeTools')}</li>
              <li>{t('oauth.consent.scopeSame')}</li>
              <li>{t('oauth.consent.scopeRevoke')}</li>
            </ul>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => decide('deny')}
              disabled={busy !== null || redirecting}
              className="w-1/3 rounded-[10px] px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
              style={{
                background: 'rgba(148, 163, 184, 0.12)',
                color: '#CBD5E1',
                border: '1px solid rgba(148, 163, 184, 0.2)',
              }}
            >
              {t('oauth.consent.deny')}
            </button>
            <button
              type="button"
              onClick={() => decide('approve')}
              disabled={busy !== null || redirecting}
              className="login-submit w-2/3 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy === 'approve' || redirecting
                ? t('oauth.consent.redirecting')
                : t('oauth.consent.approve')}
            </button>
          </div>
        </div>
      )}

      <p className="text-center text-xs" style={{ color: '#64748B' }}>
        {t('oauth.consent.footer')}
      </p>
    </AuthShell>
  );
}
